import { Box } from '@ui-lib';
import { type ReactElement, createContext, useContext, useEffect, useRef, useState } from 'react';

import { tab, tabContainer, tabUnderline } from './Tab.css';

type UnderlinePosition = {
  width: number;
  left: number;
};

type TabContext = {
  selectedTabId?: string;
  onTabChange?: (selectedTabId: string) => void;
  disabled?: boolean;
  setUnderlinePosition?: (position: UnderlinePosition) => void;
};

const TabContext = createContext<TabContext>({});

type TabProps = {
  label: string;
  id: string;
  /** Use this to render an icon inside the tab adjacent to the tab label */
  icon?: ReactElement;
  /** Used this to position the icon before or after tab label */
  iconPosition?: 'left' | 'right';
};

export const Tab: React.FC<TabProps> = ({ label, id, icon, iconPosition = 'left' }) => {
  const { onTabChange, selectedTabId, disabled, setUnderlinePosition } = useContext(TabContext) || {};
  const ref = useRef<HTMLButtonElement>(null);
  const isSelected = selectedTabId === id;

  useEffect(() => {
    const element = ref.current;

    if (element && isSelected) {
      const resizeObserver = new ResizeObserver(() => {
        setUnderlinePosition?.({
          width: element.offsetWidth,
          left: element.offsetLeft,
        });
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.unobserve(element);
      };
    }
  }, [isSelected, setUnderlinePosition]);

  return (
    <Box
      ref={ref}
      id={id}
      aria-selected={isSelected}
      role="tab"
      component="button"
      padding="medium"
      disabled={disabled}
      className={tab({ iconPosition })}
      onClick={() => onTabChange?.(id)}
    >
      {icon}
      {label}
    </Box>
  );
};

type TabsProps = {
  onTabChange?: (selectedTabId: string) => void;
  selectedTabId?: string;
  // Limit children to array of tabs or single tab
  children: ReactElement<typeof Tab> | ReactElement<typeof Tab>[];
  disabled?: boolean;
  /** Determines whether the tabs should expand to full width of the parent container */
  variant?: 'default' | 'expandedContainer' | 'expandedTabs';
};

export const Tabs: React.FC<TabsProps> = ({
  selectedTabId,
  onTabChange,
  disabled,
  variant = 'default',
  children,
}) => {
  const [underlinePosition, setUnderlinePosition] = useState<UnderlinePosition>();

  const handleOnTabChange = (id: string) => {
    onTabChange?.(id);
  };

  const handleSetUnderlinePosition = (position: UnderlinePosition) => {
    const hasPositionChanged =
      underlinePosition?.width !== position.width || underlinePosition?.left !== position.left;
    if (hasPositionChanged) setUnderlinePosition(position);
  };

  return (
    <TabContext.Provider
      value={{
        disabled,
        selectedTabId,
        onTabChange: handleOnTabChange,
        setUnderlinePosition: handleSetUnderlinePosition,
      }}
    >
      <div
        role="tablist"
        className={tabContainer({
          variant,
        })}
      >
        {children}
        <span
          style={
            underlinePosition
              ? {
                  width: underlinePosition.width,
                  transform: `translateX(${underlinePosition.left}px)`,
                }
              : {
                  display: 'none',
                }
          }
          className={tabUnderline({
            disabled,
          })}
          role="presentation"
        />
      </div>
    </TabContext.Provider>
  );
};
